import {action, computed, IReactionDisposer, makeObservable, observable, reaction, when} from "mobx";
import {
    LayerSelectorConfiguration,
    LayerSelectorGroupConfiguration,
    LayerSelectorGroupFilterConfiguration,
    LayerSelectorGroupRendererFieldConfiguration
} from "../config/layer-selector-configuration";
import {TopicConfiguration, TopicLayerConfiguration} from "../../config/topic-configuration";
import _ from "lodash";
import EsriFeatureFilter from "@arcgis/core/layers/support/FeatureFilter";
import {LayerRules, ServiceRules} from "../components/layer-selector/layer-selector";
import {PathUtils} from "../../utils/path-utils";
import {DropdownItemProps} from "semantic-ui-react/dist/commonjs/modules/Dropdown/DropdownItem";
import EsriFeatureLayer from "@arcgis/core/layers/FeatureLayer";
import EsriMapImageLayer from "@arcgis/core/layers/MapImageLayer";
import LayerView from "@arcgis/core/views/layers/LayerView";
import Layer from "@arcgis/core/layers/Layer";
import Swipe from "@arcgis/core/widgets/Swipe";
import {IAppStore} from "./abstract-app-store";

export class LayerSelectorStore {
    /**
     * If the selected options are initialized per subtopic
     */
    initialized = observable.map<string, boolean>();
    comparisonEnabled: boolean;
    config: LayerSelectorConfiguration[] | undefined;

    store: IAppStore | undefined;

    /**
     * Selected subtopic
     */
    subtopicId: string = "";
    subtopic: TopicConfiguration | undefined;

    /**
     * Dropdown options for each group and topic
     */
    dropDownOptions = observable.map<string, Map<string, DropdownItemProps[]>>();

    /**
     * The selected options for the leading map, per topic and group
     */
    selectedOptionsLeading = observable.map<string, Map<string, string>>();
    /**
     * The selected options for the trailing map, per topic and group
     */
    selectedOptionsTrailing = observable.map<string, Map<string, string>>();

    disposeSelectionInitializer: IReactionDisposer | undefined;

    /**
     * Layers used for comparison (trailing layers)
     * Duplicated from leading layers
     */
    comparisonLayers: __esri.Layer[] | undefined;

    /**
     * Layer views of comparison (trailing layers)
     */
    comparisonLayerViews: __esri.LayerView[] | undefined;

    swipeWidget: Swipe | undefined;

    constructor(store: IAppStore, config: LayerSelectorConfiguration[]) {
        makeObservable(this, {
            initialized: observable,
            comparisonEnabled: observable,
            subtopicId: observable,
            dropDownOptions: observable,
            selectedOptionsLeading: observable,
            selectedOptionsTrailing: observable,

            setInitialized: action,
            setComparisonState: action,
            setSubtopic: action,
            setDropdownOptions: action,
            setSelectedOption: action,
            matrixConfig: computed,
            isInitialized: computed,
            dropdownOptionsForCurrentTopic: computed
        });
        this.setComparisonState(false);
        this.store = store;
        this.config = config;

        this.disposeSelectionInitializer = reaction(
            () => this.subtopicId,
            () => {
                if (!this.subtopicId || !this.matrixConfig || !this.store?.mapStore.view) {
                    return;
                }

                this.initialize(this.matrixConfig);
                if (!this.isInitiallyLoading) {
                    // only apply rules directly when not first app start
                    // on the first app start, the rules are applied when initialized
                    this.applyMatrixRules();
                    if (this.comparisonEnabled) {
                        // disable and re-enable comparison to apply rules
                        this.disableComparison().then(() => {
                            this.enableComparison();
                        });
                    }
                }
            }
        );
        when(
            () => this.isInitialized,
            () => {
                // apply rules when initialized. is only called once
                this.applyMatrixRules();
            }
        );
    }

    get matrixConfig(): LayerSelectorConfiguration | undefined {
        if (!this.config || !this.subtopicId) {
            return;
        }
        return this.config.find((c: LayerSelectorConfiguration) =>
            c.topicIds.includes(this.subtopicId));
    }

    /**
     * If the selected options are initialized for the current subtopic
     */
    get isInitialized(): boolean {
        if (!this.subtopicId) {
            return false;
        }
        return !!this.initialized.get(this.subtopicId);
    }

    get isInitiallyLoading(): boolean {
        return this.initialized.size === 0;
    }

    get dropdownOptionsForCurrentTopic(): Map<string, DropdownItemProps[]> | undefined {
        if (!this.dropDownOptions || this.dropDownOptions.size === 0) {
            return;
        }

        if (!this.dropDownOptions.has(this.subtopicId)) {
            return;
        }
        return this.dropDownOptions.get(this.subtopicId);
    }

    setInitialized(initialized: boolean) {
        if (!this.subtopicId) {
            console.warn("LayerSelector: No subtopic set");
            return;
        }
        this.initialized.set(this.subtopicId, initialized);
    }
    setComparisonState(enabled: boolean) {
        this.comparisonEnabled = enabled;
    }
    setSubtopic(subtopic: TopicConfiguration) {
        this.subtopic = subtopic;
        this.subtopicId = subtopic.id;
    }

    setSelectedOption(group: string, selectedOptions: string, trailing: boolean = false){
        if (!this.subtopicId) {
            console.warn("LayerSelector: No subtopic set");
            return;
        }
        const storedOptions = trailing ? this.selectedOptionsTrailing : this.selectedOptionsLeading;

        if (storedOptions.has(this.subtopicId) && storedOptions.get(this.subtopicId) !== undefined) {
            const storedTopicOptions = storedOptions.get(this.subtopicId);
            // @ts-expect-error TS2532
            storedTopicOptions.set(group, selectedOptions);
        } else {
            const groupOptions = new Map<string, string>();
            groupOptions.set(group, selectedOptions);
            storedOptions.set(this.subtopicId, groupOptions);
        }
    }

    /**
     * Get selected options by current subtopic.
     * Leading options are returned by default.
     */
    getSelectedOptions(trailing: boolean = false): Map<string, string> | undefined {
        const storedOptions = trailing ?
            this.selectedOptionsTrailing : this.selectedOptionsLeading;
        if (!storedOptions || storedOptions.size === 0) {
            return;
        }

        return storedOptions.get(this.subtopicId);
    }

    /**
     * Store options for dropdowns in groups to optimize loading
     * @param group
     * @param dropdownOptions
     */
    setDropdownOptions(group: string, dropdownOptions: DropdownItemProps[]){
        if (!this.subtopicId) {
            console.warn("LayerSelector: No subtopic set");
            return;
        }

        let groupOptions: Map<string, DropdownItemProps[]>;
        if (this.dropDownOptions.has(this.subtopicId) && this.dropDownOptions.get(this.subtopicId) !== undefined) {
            // @ts-expect-error TS2532
            groupOptions = this.dropDownOptions.get(this.subtopicId);
            groupOptions.set(group, dropdownOptions);
        } else {
            groupOptions = new Map<string, DropdownItemProps[]>();
            groupOptions.set(group, dropdownOptions);
        }
        this.dropDownOptions.set(this.subtopicId, groupOptions);
    }

    /**
     * Get dropdown options by current subtopic.
     */
    getDropdownOptions(groupId: string): DropdownItemProps[] | undefined {
        if (!this.dropDownOptions || this.dropDownOptions.size === 0) {
            return;
        }

        if (!this.dropDownOptions.has(this.subtopicId) || this.dropDownOptions.get(this.subtopicId) === undefined) {
            return;
        }
        else {
            const storedTopicOptions = this.dropDownOptions.get(this.subtopicId);
            return storedTopicOptions?.get(groupId);
        }
    }

    /**
     * Get selected options for a specific group
     * @private
     */
    getSelectedGroupOptions(groupConfig: LayerSelectorGroupConfiguration, trailing: boolean): string[] | undefined {
        const selectedOptions = this.getSelectedOptions(trailing);
        if (!selectedOptions) {
            return;
        }

        if (selectedOptions.has(groupConfig.id)) {
            // already selected an option before
            // @ts-expect-error undefined, because TS does not respect the test if the key exists
            return selectedOptions.get(groupConfig.id);
        }
        else {
            // return default options
            return groupConfig.options
                .filter((gc) => trailing ? gc.defaultTrailing : gc.default)
                .map((gc) => gc.id);
        }
    }

    /**
     * Apply the rules of all selected matrix options
     * Set visible layers and apply filters specified by matrix configuration
     * @private
     */
    applyMatrixRules(trailing: boolean = false): void {
        if (!this.store?.mapStore.activeLayers || !this.matrixConfig) {
            return;
        }

        const rules = this.prepareServiceRules(trailing);
        if (rules) {
            this.applyRendererAndFilters(rules, trailing);
        }
        const visibilities = this.prepareVisibilities(trailing);
        if (visibilities) {
            this.applyLayerVisibility(visibilities, trailing);
        }
    }

    /**
     * Load options for dynamic dropdowns and store to the state
     * @private
     */
    loadDropdownOptions(): void {
        if (!this.store || !this.matrixConfig) {
            return;
        }

        // load dropdown options
        this.matrixConfig.layerMatrix.forEach((groupConfig: LayerSelectorGroupConfiguration) => {
            if (groupConfig.formFieldType !== "dropdown" || !groupConfig.store) {
                return;
            }

            const storedOptions = this.getDropdownOptions(groupConfig.id);
            if (storedOptions) {
                return storedOptions;
            }

            // not loaded yet. Load from data store
            const dataStore = this.store?.geoDataStores.get(groupConfig.store);
            dataStore?.loadItems(undefined).then((items: any) => {
                const options = items.map((feature: any) => {
                    return {
                        key: feature.guid,
                        text: feature.displayLabel,
                        value: feature.guid,
                        data: groupConfig.dataField ? feature[groupConfig.dataField] : null
                    };
                });
                this.setDropdownOptions(groupConfig.id, options);
            });
        });
    }

    /**
     * Get relevant layers for the selected options
     * @param trailing
     */
    relevantLayers(trailing: boolean = false): __esri.Layer[] | undefined {
        const rules = this.prepareServiceRules(trailing);
        if (!rules) {
            return;
        }
        return this.getLayersByRules(rules);
    }

    /**
     * Enable comparison mode and duplicate layers
     */
    enableComparison(): Promise<void> {
        return new Promise((resolve, reject) => {
            if (!this.store || !this.store.mapStore.view || !this.store.appStore.subtopic) {
                return;
            }
            const {mapStore, uiStore} = this.store;
            const view = mapStore.view as __esri.MapView;

            const matrixConfig = this.matrixConfig;
            if (!matrixConfig) {
                reject();
                return;
            }

            // find layers relevant for selected leading and trailing options
            const leadingLayers = this.relevantLayers();
            const trailingLayers = this.relevantLayers(true);

            if (!leadingLayers || !trailingLayers) {
                reject();
                return;
            }

            this.duplicateTrailingLayers(trailingLayers).then((clonedTrailingLayers) => {
                // here, the layerViews are ready but not yet present in the view.layerViews Array!!!
                if (!clonedTrailingLayers) {
                    reject();
                    throw new Error("Failed to duplicate leading layers");
                }

                this.applyMatrixRules(true);

                const swipeProps: __esri.SwipeViewModelProperties = {
                    // @ts-expect-error invalid id
                    id: "compare-widget",
                    view,
                    leadingLayers,
                    trailingLayers: clonedTrailingLayers,
                    direction: uiStore.uiOrientation === "portrait" ? "vertical" : "horizontal",
                    // Let space for sidebar in "landscape" mode
                    position: ((uiStore.uiOrientation === "landscape") ? 55 : 50)
                };
                this.swipeWidget = new Swipe(swipeProps);
                mapStore.view?.ui.add(this.swipeWidget);

                this.setComparisonState(true);

                resolve();
            });
        });
    }

    /**
     * Disable comparison mode and cleanup
     */
    disableComparison(): Promise<void> {
        return new Promise((resolve, reject) => {
            if (!this.comparisonEnabled || !this.comparisonLayers) {
                reject();
                return;
            }

            // destroy all comparison layers
            this.comparisonLayers.forEach((layer) => {
                this.store?.mapStore.map?.remove(layer);
                layer.destroy();
            });
            this.comparisonLayers = undefined;
            this.comparisonLayerViews = undefined;

            this.setComparisonState(false);

            if (this.swipeWidget) {
                this.store?.mapStore.view?.ui.remove(this.swipeWidget);
                this.swipeWidget.destroy();
            }

            resolve();
        });
    }

    /**
     * Duplicate the layers used as trailing layers
     * This enables comparison of the same layers with different filters
     * Add to a special group layer to support comparison
     * @private
     */
    duplicateTrailingLayers(leadingLayers: __esri.Layer[]): Promise<__esri.Layer[] | undefined> {
        return new Promise((resolve, reject) => {
            if (!this.store || !this.store.mapStore.view || !this.store?.appStore.subtopic) {
                reject();
                return;
            }
            const {mapStore} = this.store;
            const {view} = mapStore;

            const selectedTrailingOptions = this.getSelectedOptions(true);
            if (!selectedTrailingOptions) {
                reject();
                return;
            }

            // do not add layers when already added
            const layers = mapStore.getLayersByMetauid("[compare]");

            const layerLoadedPromises = leadingLayers.map((layer) => {
                const url = PathUtils.getLayerPath(layer);
                if (!url) {
                    // skip graphics layers
                    return;
                }
                const metauid = PathUtils.getMetaId(url);

                const layerFound = layers.find((l) => {
                    return (l as any).sourceMetauid === metauid && (l as any).layerId === (layer as any).layerId;
                });
                if (layerFound) {
                    return;
                }

                const tConfig = {
                    // take over properties from leading layer
                    blendMode: (layer as any).blendMode ? (layer as any).blendMode : "normal",
                    url,
                    legendEnabled: false,
                    // hide layer in layer list
                    //listMode: "hide",
                    visible: false, // visibility is applied later when rules are applied
                    title: `[compare] ${layer.title}`
                };

                let trailingLayer;
                if (layer.type === "feature") {
                    trailingLayer = new EsriFeatureLayer(tConfig as __esri.FeatureLayerProperties);
                }
                else if (layer.type === "map-image") {
                    trailingLayer = new EsriMapImageLayer(tConfig as __esri.MapImageLayerProperties);
                }
                if (!trailingLayer) {
                    return;
                }

                return trailingLayer.load();
            });

            // wait until all layers are loaded
            Promise.all(layerLoadedPromises).then((duplicatedLayers: Layer[]) => {
                this.comparisonLayers = duplicatedLayers;

                const layerViewPromises = duplicatedLayers.map((layer) => {
                    // complete layer properties
                    const url = PathUtils.getLayerPath(layer);
                    const metauid = PathUtils.getMetaId(url);

                    layer.set("metauid", "[compare]");
                    layer.set("sourceMetauid", metauid); // remember original layer metauid
                    if ((layer as any).sublayers) {
                        (layer as any).sublayers.forEach((l: any) => {
                            l.set("metauid", "[compare]");
                            l.set("sourceMetauid", metauid);
                            l.name = `[compare] ${l.name}`;
                            l.legendEnabled = false;
                        });
                    }

                    mapStore.map?.add(layer);

                    // @ts-expect-error view undefined
                    return view.whenLayerView(layer);
                });

                // wait until all layer views are ready
                Promise.all(layerViewPromises).then((layerViews: LayerView[]) => {
                    this.comparisonLayerViews = layerViews;
                    resolve(duplicatedLayers);
                });
            });
        });
    }

    /**
     * Update orientation of Swipe widget
     */
    updateOrientation(direction: "vertical" | "horizontal"): void {
        if(!this.comparisonEnabled || !this.swipeWidget){
            return;
        }
        this.swipeWidget.direction = direction;
    }

    private applyLayerVisibility(visibleLayers: TopicLayerConfiguration[], trailing: boolean = false) {
        if (!this.store || !this.subtopic) {
            return;
        }
        if (visibleLayers.length === 0) {
            console.debug("LayerSelector: No visible layers");
            return;
        }
        const {subtopic} = this;

        if (!trailing) {
            // manipulate active layers of current topic and reapply
            // clone active layers to avoid setting the same object reference
            const mapActiveLayers = _.cloneDeep(subtopic?.layers);
            if (mapActiveLayers) {
                mapActiveLayers.visible = visibleLayers;
                this.store.appStore.setSubtopicLayers(mapActiveLayers);
            }
        }
        else {
            // // trailing layers cannot be handled by topic functions
            // const trailingLayers = this.store.mapStore?.getLayersByMetauid("[compare]");

            if (!this.comparisonLayers) {
                return;
            }

            this.comparisonLayers.forEach((layer) => {
                const metauid = (layer as any).sourceMetauid;

                if (layer.type === "feature") {
                    layer.visible = !!visibleLayers?.find((c) => {
                        const layerId = (layer as any).layerId;
                        return !!(metauid === c.id && c.layerIds?.includes(layerId));
                    });
                }
                else if ((layer as any).type === "sublayer") {
                    layer.visible = !!visibleLayers?.find((c) => {
                        const layerId = (layer as any).id;
                        return !!(metauid === c.id && c.layerIds?.includes(layerId));
                    });
                }
                else if (layer.type === "map-image") {
                    let anySubLayerVisible = false;
                    (layer as EsriMapImageLayer).sublayers?.forEach((subLayer) => {
                        const isSublayerVisible = visibleLayers?.find((c) => {
                            return metauid === c.id && c.layerIds?.includes(subLayer.id);
                        });
                        if (isSublayerVisible) {
                            subLayer.visible = true;
                            anySubLayerVisible = true;
                        } else {
                            subLayer.visible = false;
                        }
                    });
                    layer.visible = anySubLayerVisible;
                }
            });
        }
    }

    private applyRendererAndFilters(serviceRules: ServiceRules, trailing: boolean = false): void {
        if (!this.store || !this.store.mapStore.view || serviceRules.size === 0) {
            return;
        }

        // if tailing, only apply to layers with METAUID "[compare]"
        serviceRules.forEach((layerRules: Map<number, LayerRules>, serviceId: string) => {
            const layerIds = Array.from(layerRules.keys());

            // find layer views for this rule
            const layerViews = trailing?
                this.comparisonLayerViews : this.store?.mapStore.getLayerViewsByMetauid(serviceId, layerIds);

            if (!layerViews || layerViews.length === 0) {
                // do not throw error here. In some cases renderer and filters are applied twice (why?) and in
                // second turn the layerViews are not found. But the filters and renderers are applied correctly
                // in the first turn.
                return;
            }

            layerRules.forEach((rules: LayerRules, layerId: number) => {
                const layerView = this.getLayerView(layerViews, serviceId, layerId, trailing);
                if (!layerView) {
                    throw new Error(`Layer view not found ${serviceId} ${layerId}`);
                }

                if (layerView.layer.type === "feature") {
                    // change renderer field
                    if (rules.renderField) {
                        (layerView.layer.renderer as any).field = rules.renderField;
                    }

                    // apply filter
                    if (!layerView.filter) {
                        layerView.filter = new EsriFeatureFilter();
                    }
                    if (rules.filter && rules.filter.length > 0) {
                        // replace {renderField} in filter if exists
                        const filter = rules.filter.replace("{renderField}", (layerView.layer.renderer as any).field);
                        layerView.filter.where = filter;
                    } else {
                        layerView.filter.where = "1=1";
                    }
                }
                else if (layerView.layer.type === "map-image") {
                    // apply filter
                    const mapImageLayer = layerView.layer as __esri.MapImageLayer;
                    const subLayer = mapImageLayer?.sublayers.find((l) =>
                        l.id === layerId
                    );
                    if (!subLayer) {
                        return;
                    }
                    subLayer.definitionExpression = rules.filter || "1=1";
                }
            });

        });
    }

    private getLayerView(layerViews: __esri.LayerView[], metauid: string, layerId: number, trailing: boolean = false) {
        // get layer view
        // for trailing layers, the METAUID of the source layer is stored in property "sourceMetauid"
        const layerView: any = layerViews.find((lw) =>
            (
                trailing ? (lw.layer as any).sourceMetauid : (lw.layer as any).metauid === metauid ||
                PathUtils.getMetaId(PathUtils.getLayerPath(lw.layer)) === metauid
            ) &&
            (
                (lw.layer as __esri.FeatureLayer).layerId === layerId ||
                (lw.layer as __esri.MapImageLayer).sublayers?.map((l) => l.id).includes(layerId)
            )
        );
        return layerView;
    }

    /**
     * Prepare rules for selected options
     * Summarize all rules per service and layer
     * @private
     */
    private prepareServiceRules(trailing?: boolean): ServiceRules | undefined {
        if (!this.store || !this.matrixConfig) {
            return;
        }

        const selectedOptions= this.getSelectedOptions(trailing);
        if (!selectedOptions) {
            return;
        }

        const {matrixConfig} = this;

        // loop through selected options and extract rules per service and layer
        const rules: ServiceRules = new Map<string, Map<number, LayerRules>>();
        selectedOptions.forEach((value: string, groupId: string) => {
            // get config
            const groupConfig: LayerSelectorGroupConfiguration | undefined =
                matrixConfig.layerMatrix.find((gc: LayerSelectorGroupConfiguration) => gc.id === groupId);
            const groupOptionConfigs = groupConfig?.options.find((o) => {
                return o.id === value || o.type === "template";
            });
            if (!groupOptionConfigs) {
                return;
            }

            // add rules for layers
            if (groupOptionConfigs.layers) {
                groupOptionConfigs.layers.forEach((layerConfig: TopicLayerConfiguration) => {
                    // initialize rules for service
                    rules.has(layerConfig.id) || rules.set(layerConfig.id, new Map<number, LayerRules>());
                    const layerRules = rules.get(layerConfig.id);

                    layerConfig.layerIds?.forEach((layerId) => {
                        const layerRule = layerRules?.get(layerId) || {};
                        layerRule.visible = true;
                        layerRules?.set(layerId, layerRule);
                    });
                });
            }
            // add filter rules
            if (groupOptionConfigs.filters) {
                groupOptionConfigs.filters.forEach((filterConfig: LayerSelectorGroupFilterConfiguration) => {
                    // initialize rules for service
                    rules.has(filterConfig.id) || rules.set(filterConfig.id, new Map<number, LayerRules>());
                    const layerRules = rules.get(filterConfig.id);

                    filterConfig.layerIds?.forEach((layerId) => {
                        const layerRule = layerRules?.get(layerId) || {};

                        let whereClause: string ;
                        if (filterConfig.where.includes("{value}")) {
                            // replace value if needed. Value is replaced for dynamically loaded options
                            // where value is not known at configuration time
                            if (value) {
                                whereClause = filterConfig.where.replace("{value}", value);
                            }
                            else {
                                // no value selected, remove filter for this option
                                whereClause = "";
                            }
                        }
                        else {
                            // no dynamic field, use filter as is
                            whereClause = filterConfig.where;
                        }
                        // concat filters if already set
                        if (layerRule && layerRule.filter && layerRule.filter.length > 0) {
                            if (whereClause.length > 0) {
                                layerRule.filter += ` AND ${whereClause}`;
                            }
                        }
                        else {
                            layerRule.filter = whereClause;
                        }
                        layerRules?.set(layerId, layerRule);
                    });
                });
            }
            // add render field rules
            if (groupOptionConfigs.rendererFields) {
                groupOptionConfigs.rendererFields.forEach((rendererFieldConfig: LayerSelectorGroupRendererFieldConfiguration) => {
                    // initialize rules for service
                    rules.has(rendererFieldConfig.id) || rules.set(rendererFieldConfig.id, new Map<number, LayerRules>());
                    const layerRules = rules.get(rendererFieldConfig.id);

                    rendererFieldConfig.layerIds?.forEach((layerId) => {
                        const layerRule = layerRules?.get(layerId) || {};
                        layerRule.renderField = rendererFieldConfig.fieldName;
                        layerRules?.set(layerId, layerRule);
                    });
                });
            }
        });
        console.debug("Rules", rules);
        return rules;
    }

    /**
     * Prepare visibilities for selected options separately, because these are needed as TopicLayerConfiguration[]
     * @param trailing
     * @private
     */
    private prepareVisibilities(trailing?: boolean): TopicLayerConfiguration[] | undefined {
        if (!this.store || !this.matrixConfig) {
            return;
        }
        const selectedOptions = this.getSelectedOptions(trailing);
        if (!selectedOptions) {
            return;
        }

        const {matrixConfig} = this;

        // loop through selected options and extract rules per service and layer
        let visibilities: TopicLayerConfiguration[] = [];
        selectedOptions.forEach((value: string, groupId: string) => {
            // get config
            const groupConfig: LayerSelectorGroupConfiguration | undefined =
                matrixConfig.layerMatrix.find((gc: LayerSelectorGroupConfiguration) => gc.id === groupId);
            const groupOptionConfigs = groupConfig?.options.find((o) => o.id === value);
            if (!groupOptionConfigs) {
                return;
            }
            if (groupOptionConfigs.layers) {
                visibilities = visibilities.concat(groupOptionConfigs.layers);
            }
        });
        return visibilities;
    }

    /**
     * Initialize the selected options based on the default options.
     * Initialize the rules for all subtopics the first time a subtopic is set
     * @param matrixConfig
     * @private
     */
    private initialize(matrixConfig: LayerSelectorConfiguration) {
        if (this.isInitialized) {
            return;
        }
        matrixConfig.layerMatrix.forEach((groupConfig: LayerSelectorGroupConfiguration) => {
            const leadingOption = groupConfig.options.find((o) => o.default)?.id;
            if (leadingOption) {
                this.setSelectedOption(groupConfig.id, leadingOption);
            }
            const trailingOption = groupConfig.options.find((o) => o.defaultTrailing)?.id;
            if (trailingOption) {
                this.setSelectedOption(groupConfig.id, trailingOption, true);
            }
        });

        const rules = this.prepareServiceRules();
        if (!rules) {
            return;
        }
        const layers = this.getLayersByRules(rules);

        const view = this.store?.mapStore.view;
        if (!view) {
            return;
        }

        const viewReadyHandles = layers.map((layer) => {
            return view.whenLayerView(layer);
        });
        // wait for all layer views to be ready until setting initialized to true
        // matrix rules will be applied in the reaction (see constructor)
        Promise.all(viewReadyHandles).then(() => {
            this.setInitialized(true);
        }).catch((e) => {
            console.error("Error waiting for layer views", e);
        });
    }

    /**
     * Get all layers handled by the rules. This is needed to wait for all layer views to be ready
     * @param rules
     * @private
     */
    private getLayersByRules(rules: Map<string, Map<number, LayerRules>>) {
        // get all layers from selected options and wait for layer views to be ready
        let layers: __esri.Layer[] = [];
        rules.forEach((layerRules: Map<number, LayerRules>, serviceId: string) => {
            const layerIds = Array.from(layerRules.keys());
            // find layer views for this rule
            const serviceLayers: __esri.Layer[] | undefined =
                this.store?.mapStore.getLayersByMetauid(serviceId, layerIds);

            if (!serviceLayers || serviceLayers.length === 0) {
                console.error("No layer views found. Layer views not ready yet?");
                return;
            }
            layers = layers.concat(serviceLayers);
        });
        return layers;
    }
}
